import {
  Add,
  ContactMail,
  Delete,
  Language,
  PhoneAndroid,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { Box, Button, Grid, IconButton, Paper, Tooltip } from "@mui/material";
import CustomTextField from "../../../../components/common/Textfield";
import PhoneNumberField from "../../../../components/common/PhoneNumberField"; // adjust path to match where you saved it
import SectionHeader from "./SectionHeader";

const uid = () => Math.random().toString(36).slice(2, 9);

export interface ContactDetail {
  id: string;
  type: "email" | "phone" | "address";
  value: string;
}

interface Props {
  contactDetails: ContactDetail[];
  setContactDetails: React.Dispatch<React.SetStateAction<ContactDetail[]>>;
  primaryColor: string;
  secondaryColor: string;
  bgColor: string;
}

const ContactPanel: React.FC<Props> = ({
  contactDetails,
  setContactDetails,
  primaryColor,
  secondaryColor,
  bgColor,
}) => {
  const typeIcon = (type: ContactDetail["type"]) => {
    if (type === "email")
      return <ContactMail sx={{ color: secondaryColor, fontSize: 20 }} />;
    if (type === "phone")
      return <PhoneAndroid sx={{ color: secondaryColor, fontSize: 20 }} />;
    return <Language sx={{ color: secondaryColor, fontSize: 20 }} />;
  };

  const addContact = () =>
    setContactDetails((prev) => [
      ...prev,
      { id: uid(), type: "email", value: "" },
    ]);

  const updateContact = (
    id: string,
    field: keyof ContactDetail,
    value: string,
  ) =>
    setContactDetails((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );

  const removeContact = (id: string) =>
    setContactDetails((prev) => prev.filter((c) => c.id !== id));

  return (
    <Box>
      <SectionHeader title="Contact Details" color={primaryColor} />
      <AnimatePresence>
        {contactDetails.map((contact) => (
          <motion.div
            key={contact.id}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Paper
              sx={{
                p: 2,
                mb: 2,
                boxShadow: 0,
                backgroundColor: bgColor,
                borderRadius: 2,
                border: `1px solid ${primaryColor}22`,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {typeIcon(contact.type)}
                    <CustomTextField
                      select
                      fullWidth
                      size="small"
                      value={contact.type}
                      onChange={(e) =>
                        updateContact(contact.id, "type", e.target.value)
                      }
                      SelectProps={{ native: true }}
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="address">Address</option>
                    </CustomTextField>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 9 }}>
                  {contact.type === "phone" ? (
                    <PhoneNumberField
                      size="small"
                      value={contact.value}
                      onChange={(value) =>
                        updateContact(contact.id, "value", value)
                      }
                    />
                  ) : (
                    <CustomTextField
                      fullWidth
                      size="small"
                      placeholder={
                        contact.type === "email"
                          ? "hello@company.com"
                          : "Street, City, Country"
                      }
                      value={contact.value}
                      onChange={(e) =>
                        updateContact(contact.id, "value", e.target.value)
                      }
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }}>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => removeContact(contact.id)}
                      sx={{ color: "error.main" }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>
        ))}
      </AnimatePresence>
      <Button
        startIcon={<Add />}
        variant="outlined"
        size="small"
        onClick={addContact}
        sx={{
          borderColor: primaryColor,
          color: primaryColor,
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          "&:hover": { backgroundColor: `${primaryColor}12` },
        }}
      >
        Add Contact Detail
      </Button>
    </Box>
  );
};

export default ContactPanel;